import React, { useEffect, useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Image as ImageIcon,
  X,
  Save,
  Loader,
  Eye
} from 'lucide-react';
import config from '../utils/config';
import Swal from 'sweetalert2';

export default function GalleryAdmin() {
  const [gallery, setGallery] = useState([]);
  const [form, setForm] = useState({ 
    title: '', 
    description: '', 
    category: ''
  });
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    'events',
    'service',
    'community',
    'awards',
    'other'
  ];

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await fetch(`${config.url}/gallery`);
      const data = await response.json();
    setGallery(data);
    } catch (error) {
      setError('Failed to fetch gallery');
    }
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const clearForm = () => {
    setForm({ title: '', description: '', category: '' });
    setEditing(null);
    setImageFile(null);
    setImagePreview('');
    setError('');
    setSuccess('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('title', form.title.trim());
      formData.append('description', form.description.trim());
      formData.append('category', form.category);
      
      // Handle image upload or existing image
      if (imageFile) {
        formData.append('image', imageFile);
      } else if (imagePreview && editing) {
        // Keep existing image if no new file is selected during edit
        const existingImageUrl = imagePreview.replace(`${config.url}`, '');
        formData.append('image_url', existingImageUrl);
      }

      const token = localStorage.getItem('adminToken');
      const url = editing 
        ? `${config.url}/gallery/${editing}`
        : `${config.url}/gallery`;
      
      const response = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(editing ? 'Gallery item updated successfully!' : 'Gallery item created successfully!');
        clearForm();
        setShowForm(false);
        fetchGallery();
    } else {
        setError(data.error || 'Operation failed');
    }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = item => {
    setForm({ 
      title: item.title || '', 
      description: item.description || '', 
      category: item.category || ''
    });
    setEditing(item.id);
    setImagePreview(item.image_url ? `${config.url}${item.image_url}` : '');
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async id => {
    const result = await Swal.fire({
      title: 'Delete this gallery item?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    });
    if (!result.isConfirmed) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${config.url}/gallery/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('Gallery item deleted successfully!');
    fetchGallery();
      } else {
        setError('Failed to delete gallery item');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }
  };

  const filteredGallery = selectedCategory === 'all' 
    ? gallery 
    : gallery.filter(item => item.category === selectedCategory);

  return (
    <div className="space-y-6 hindi-text">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gallery Management</h1>
          <p className="text-gray-600">Manage your photo gallery and images</p>
        </div>
        <button
          onClick={() => {
            clearForm();
            setShowForm(true);
          }}
          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>Upload Image</span>
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700">{success}</p>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            selectedCategory === 'all'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All
        </button>
        {categories.map(category => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedCategory === category
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {editing ? 'Edit Gallery Item' : 'Upload New Image'}
            </h2>
            <button
              onClick={() => {
                setShowForm(false);
                clearForm();
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image Title *
                </label>
                <input
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Enter image title"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">Select category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Enter image description"
              />
            </div>

    <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image File *
              </label>
              <div className="space-y-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required={!editing}
                />
                {imagePreview && (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-lg border"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview('');
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  clearForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center space-x-2 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>{editing ? 'Update' : 'Upload'} Image</span>
              </button>
            </div>
      </form>
        </div>
      )}

      {/* Gallery Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredGallery.map(item => (
          <div key={item.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group">
            <div className="relative">
              {item.image_url && (
                <img 
                  src={`${config.url}${item.image_url}`} 
                  alt={item.title}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              )}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex space-x-2">
                  <button
                    onClick={() => handleEdit(item)}
                    className="bg-white bg-opacity-90 p-2 rounded-full hover:bg-opacity-100 transition-colors"
                  >
                    <Edit className="w-4 h-4 text-purple-600" />
                  </button>
                  <button
                    onClick={() => window.open(`${config.url}${item.image_url}`, '_blank')}
                    className="bg-white bg-opacity-90 p-2 rounded-full hover:bg-opacity-100 transition-colors"
                  >
                    <Eye className="w-4 h-4 text-purple-600" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="bg-white bg-opacity-90 p-2 rounded-full hover:bg-opacity-100 transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>
              {item.category && (
                <div className="absolute top-2 left-2 bg-purple-600 text-white px-2 py-1 rounded text-xs font-medium">
                  {item.category}
                </div>
              )}
            </div>
            <div className="p-4">
              <h3 className="font-bold text-gray-900 mb-1 line-clamp-1">{item.title}</h3>
              {item.description && (
                <p className="text-gray-600 text-sm line-clamp-2">{item.description}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredGallery.length === 0 && !showForm && (
        <div className="text-center py-12">
          <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No gallery items yet</h3>
          <p className="text-gray-600 mb-4">Get started by uploading your first image</p>
          <button
            onClick={() => {
              clearForm();
              setShowForm(true);
            }}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 mx-auto"
          >
            <Plus className="w-5 h-5" />
            <span>Upload Image</span>
          </button>
        </div>
      )}
    </div>
  );
} 